import './entity-photos.professional-caregiver.screen.scss';
import {Alert, Box, Button, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation, useParams, useRouteMatch} from 'react-router-dom';
import {translations} from '../../../../../../i18n/translations';
import {ScreenPaths} from '../../../../../../enums/screen-path';
import {useState} from 'react';
import {MediaGallery} from '../../../../../../components/media-gallery/media-gallery';
import {MediaGalleryType} from '../../../../../../enums/media-gallery-type';
import {getBrandName} from '../../../../../../utils/functions/get-product-name';
import {useAppContext} from '../../../../../../contexts/app/context';
import AddIcon from '@mui/icons-material/Add';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {Modal} from '../../../../../../components/modal/modal';

interface EntityPhotosProfessionalCaregiverOfficeScreenProps {
  canWrite: boolean;
}

export default function EntityPhotosProfessionalCaregiverOfficeScreen({
  canWrite,
}: EntityPhotosProfessionalCaregiverOfficeScreenProps) {
  let {entityId}: {entityId?: string} = useParams();
  const appContext = useAppContext();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url, path} = useRouteMatch();
  const {t} = useTranslation();
  let history = useHistory();

  // Hold state
  const [deletedPhotoId, setDeletePhotoId] = useState<string | null>(
    new URLSearchParams(useLocation().search).get(ScreenPaths.EntityPhotosDeletedQuery)
  );

  // Handle close dialog
  const handleCloseDialog = () => {
    // Reset state
    setDeletePhotoId(null);

    // Reset history
    history.replace(path.replace(':entityId', entityId || ''));
  };

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <GenericHeader
        titleCrumbs={[
          {
            label: t(translations.office.professional_caregiver.tabs.digital_inclusion),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`,
          },
          {
            label: t(translations.office.professional_caregiver.tabs.entity_photos),
            link: `${url}`,
          },
        ]}
        isLoading={false}
        leftColumn={<></>}
        rightColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingTop: '10px',
            }}
          >
            <Button
              variant="inverted"
              endIcon={<AddIcon />}
              onClick={() =>
                history.push(url.charAt(url.length - 1) === '/' ? `${url}create` : `${url}/create`)
              }
              disabled={!canWrite}
            >
              {t(translations.entity_photos.root.add_button)}
            </Button>
          </Stack>
        }
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <Alert className="entity-photos-alert" severity="info">
            {t(translations.entity_photos.root.entity_photos_only_for_computer_info_message)}
          </Alert>
          <MediaGallery
            type={MediaGalleryType.EntityMedia}
            entityId={entityId}
            baseMediaUrl={url}
          />
        </Box>
      </Box>
      <Modal
        open={deletedPhotoId ? true : false}
        className="entity-photo-modal"
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        preventCloseClick
        actions={[
          {
            label: t(translations.modal.ok),
            type: 'nondestructive',
            onClick: handleCloseDialog,
          },
        ]}
      >
        <Stack>
          <Typography className="title">
            {t(translations.entity_photos.root.deleted_photo_dialog_title)}
          </Typography>
          <Typography className="subtitle">
            {t(translations.entity_photos.root.deleted_photo_dialog_message, {
              brand: getBrandName(appContext.state.configuration.product),
            })}
          </Typography>
        </Stack>
      </Modal>
    </Box>
  );
}
